import { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Alert, Row, Col } from "react-bootstrap";
import Api from "../../../Services/Api";

interface Treatment {
  id: number;
  name: string;
  medicationId: number;
  treatmentPeriodicityId: number;
  routeOfAdministration: string;
  treatmentDurationDays: number;
  isTemplate: boolean;
  notes?: string;
  takeQuantity?: number | null;
  medication?: {
    id: number;
    name: string;
  };
  treatmentPeriodicity?: {
    id: number;
    timeInterval: number;
    timeIntervalUnit?: {
      id: number;
      name: string;
      hoursDuration: number;
    };
  };
  reminders?: { id?: number; hour: number; minute: number }[];
  createdAt?: string;
  updatedAt?: string;
}

interface Reminder {
  hour: number;
  minute: number;
}

const Treatments = () => {
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [medications, setMedications] = useState<any[]>([]);
  const [treatmentPeriodicities, setTreatmentPeriodicities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState<Treatment | null>(null);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  
  const [formData, setFormData] = useState({
    name: "",
    medicationId: 0,
    treatmentPeriodicityId: 0,
    routeOfAdministration: "",
    treatmentDurationDays: 0,
    isTemplate: false,
    notes: "",
    takeQuantity: null as number | null,
  });

  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [newReminder, setNewReminder] = useState<Reminder>({ hour: 9, minute: 0 });
  const [addingReminder, setAddingReminder] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const api = new Api();
    const [treatmentsData, medicationsData, periodicitiesData] = await Promise.all([
      api.getTreatments(),
      api.getMedications(),
      api.getTreatmentPeriodicities(),
    ]);
    if (treatmentsData) {
      setTreatments(treatmentsData);
    }
    if (medicationsData) {
      setMedications(medicationsData);
    }
    if (periodicitiesData) {
      setTreatmentPeriodicities(periodicitiesData);
    }
    setLoading(false);
  };

  const handleOpenCreate = () => {
    setEditingTreatment(null);
    setFormData({
      name: "",
      medicationId: 0,
      treatmentPeriodicityId: 0,
      routeOfAdministration: "",
      treatmentDurationDays: 0,
      isTemplate: false,
      notes: "",
      takeQuantity: null,
    });
    setReminders([]);
    setNewReminder({ hour: 9, minute: 0 });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const handleOpenEdit = async (treatment: Treatment) => {
    setEditingTreatment(treatment);
    // Handle both camelCase and snake_case from API
    const takeQuantity = (treatment as any).take_quantity ?? treatment.takeQuantity ?? null;
    setFormData({
      name: treatment.name,
      medicationId: treatment.medicationId,
      treatmentPeriodicityId: treatment.treatmentPeriodicityId,
      routeOfAdministration: treatment.routeOfAdministration,
      treatmentDurationDays: treatment.treatmentDurationDays,
      isTemplate: treatment.isTemplate,
      notes: treatment.notes || "",
      takeQuantity: takeQuantity,
    });
    const api = new Api();
    const remindersData = await api.getTreatmentReminders(treatment.id);
    const rawList = Array.isArray(remindersData) ? remindersData : remindersData?.reminders ?? [];
    const existingReminders = (Array.isArray(rawList) ? rawList : []).map((r: { hour: number; minute: number }) => ({
      hour: r.hour,
      minute: r.minute,
    })).sort((a: { hour: number; minute: number }, b: { hour: number; minute: number }) => {
      return a.hour - b.hour || a.minute - b.minute;
    });
    setReminders(existingReminders);
    setNewReminder({ hour: 9, minute: 0 });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingTreatment(null);
    setFormData({
      name: "",
      medicationId: 0,
      treatmentPeriodicityId: 0,
      routeOfAdministration: "",
      treatmentDurationDays: 0,
      isTemplate: false,
      notes: "",
      takeQuantity: null,
    });
    setReminders([]);
    setNewReminder({ hour: 9, minute: 0 });
    setError("");
    setSuccess("");
  };

  const handleAddReminder = async () => {
    if (!editingTreatment) return;
    const { hour, minute } = newReminder;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      setError("Hour must be 0-23 and minute must be 0-59");
      return;
    }
    setAddingReminder(true);
    setError("");
    try {
      const api = new Api();
      await api.createTreatmentReminder({
        treatmentId: editingTreatment.id,
        hour,
        minute,
      });
      setReminders((prev) => [...prev, { hour, minute }]);
      setNewReminder({ hour: 9, minute: 0 });
      setSuccess("Reminder added successfully");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to add reminder");
    } finally {
      setAddingReminder(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.name || !formData.medicationId || !formData.treatmentPeriodicityId || 
        !formData.routeOfAdministration || formData.treatmentDurationDays <= 0) {
      setError("Please fill in all required fields with valid values");
      return;
    }

    try {
      const api = new Api();
      const submitData: any = {
        ...formData,
        notes: formData.notes || undefined,
      };
      // Send take_quantity in snake_case for the API
      // Only include it if it has a value (including 0)
      if (formData.takeQuantity !== null && formData.takeQuantity !== undefined) {
        submitData.take_quantity = formData.takeQuantity;
      } else {
        // For updates, explicitly set to null if it was cleared
        submitData.take_quantity = null;
      }
      // Remove camelCase version if it exists
      // delete submitData.takeQuantity;
      if (editingTreatment) {

        console.log('formData', formData);
        console.log('submitData', submitData);
        await api.updateTreatment(editingTreatment.id, submitData);
        setSuccess("Treatment updated successfully");
      } else {
        await api.createTreatment(submitData);
        setSuccess("Treatment created successfully");
      }
      handleClose();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this treatment?")) {
      return;
    }

    try {
      const api = new Api();
      await api.deleteTreatment(id);
      setSuccess("Treatment deleted successfully");
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const getPeriodicityDisplay = (periodicity: any) => {
    if (periodicity?.timeIntervalUnit) {
      return `Every ${periodicity.timeInterval} ${periodicity.timeIntervalUnit.name}`;
    }
    return `Periodicity ID: ${periodicity?.id || 'N/A'}`;
  };

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h3>Treatments</h3>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleOpenCreate}>
            Create New
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Medication</th>
            <th>Periodicity</th>
            <th>Route</th>
            <th>Duration (Days)</th>
            <th>Take Quantity</th>
            <th>Template</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {treatments.map((treatment) => (
            <tr key={treatment.id}>
              <td>{treatment.id}</td>
              <td>{treatment.name}</td>
              <td>
                {treatment.medication ? treatment.medication.name : `Medication ID: ${treatment.medicationId}`}
              </td>
              <td>
                {treatment.treatmentPeriodicity 
                  ? getPeriodicityDisplay(treatment.treatmentPeriodicity)
                  : `Periodicity ID: ${treatment.treatmentPeriodicityId}`}
              </td>
              <td>{treatment.routeOfAdministration}</td>
              <td>{treatment.treatmentDurationDays}</td>
              <td>{(() => {
                const takeQty = (treatment as any).take_quantity ?? treatment.takeQuantity;
                return takeQty !== null && takeQty !== undefined ? Number(takeQty).toFixed(2) : "-";
              })()}</td>
              <td>{treatment.isTemplate ? "Yes" : "No"}</td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleOpenEdit(treatment)}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(treatment.id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTreatment ? "Edit Treatment" : "Create Treatment"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Medication</Form.Label>
              <Form.Select
                value={formData.medicationId}
                onChange={(e) => setFormData({ ...formData, medicationId: parseInt(e.target.value) })}
                required
              >
                <option value={0}>Select a medication</option>
                {medications.map((medication) => (
                  <option key={medication.id} value={medication.id}>
                    {medication.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Treatment Periodicity</Form.Label>
              <Form.Select
                value={formData.treatmentPeriodicityId}
                onChange={(e) => setFormData({ ...formData, treatmentPeriodicityId: parseInt(e.target.value) })}
                required
              >
                <option value={0}>Select a periodicity</option>
                {treatmentPeriodicities.map((periodicity) => (
                  <option key={periodicity.id} value={periodicity.id}>
                    {getPeriodicityDisplay(periodicity)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Route of Administration</Form.Label>
              <Form.Select
                value={formData.routeOfAdministration}
                onChange={(e) => setFormData({ ...formData, routeOfAdministration: e.target.value })}
                required
              >
                <option value="">Select route of administration...</option>
                <option value="oral">Oral</option>
                <option value="sublingual">Sublingual</option>
                <option value="intravenous">Intravenous</option>
                <option value="subcutaneous">Subcutaneous</option>
                <option value="intramuscular">Intramuscular</option>
                <option value="topical">Topical</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Treatment Duration (Days)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.treatmentDurationDays}
                onChange={(e) => setFormData({ ...formData, treatmentDurationDays: parseInt(e.target.value) || 0 })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Take Quantity (Optional)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={formData.takeQuantity ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    setFormData({ ...formData, takeQuantity: null });
                  } else {
                    const numValue = parseFloat(value);
                    console.log('value to take quantitys', value, isNaN(numValue) ? null : numValue);
                    setFormData({ ...formData, takeQuantity: isNaN(numValue) ? null : numValue });
                  }
                }}
                placeholder="Enter quantity"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Is Template"
                checked={formData.isTemplate}
                onChange={(e) => setFormData({ ...formData, isTemplate: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Form.Group>

            {editingTreatment && (
              <Form.Group className="mb-3">
                <Form.Label>Reminders</Form.Label>
                <p className="text-muted small mb-2">Add reminder times for this treatment (e.g., 9:00 AM).</p>
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <Form.Control
                    type="number"
                    min={0}
                    max={23}
                    style={{ width: 70 }}
                    value={newReminder.hour}
                    onChange={(e) =>
                      setNewReminder({ ...newReminder, hour: Math.min(23, Math.max(0, parseInt(e.target.value) || 0)) })
                    }
                    placeholder="Hr"
                  />
                  <span>:</span>
                  <Form.Control
                    type="number"
                    min={0}
                    max={59}
                    style={{ width: 70 }}
                    value={newReminder.minute}
                    onChange={(e) =>
                      setNewReminder({ ...newReminder, minute: Math.min(59, Math.max(0, parseInt(e.target.value) || 0)) })
                    }
                    placeholder="Min"
                  />
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddReminder}
                    disabled={addingReminder}
                  >
                    {addingReminder ? "Adding..." : "Add Reminder"}
                  </Button>
                </div>
                {reminders.length > 0 && (
                  <ul className="list-unstyled mb-0">
                    {reminders.map((r, idx) => (
                      <li key={idx} className="text-muted small">
                        {String(r.hour).padStart(2, "0")}:{String(r.minute).padStart(2, "0")}
                      </li>
                    ))}
                  </ul>
                )}
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingTreatment ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Treatments;
